/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Chip,
  Typography,
  Box
} from '@mui/material';
import { Form, FormikProps } from 'formik';
import FormikForm from 'components/layout/FormikForm';
import FormField from 'components/generals/FormInput';
import FormPhoneInput from 'components/generals/FormPhoneInput';
import Button from 'components/generals/Button';
import Autocomplete from 'components/generals/Autocomplete';
import ResourceTable from 'components/tables/ResourceListModalTable';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ResourceInterface, ProviderInterface, QueryResultInterface, HeaderEnum } from 'medicalsystem-shared';
import { resourceFormYUPValidation, validatePhoneInputSymbol } from 'utils/form.utils';
import { getNewResourceDataContent } from 'utils/model.utils';
import { AutoCompleteOption } from 'types';
import { CoreAPI_URLs } from 'constants/http.const';
import useHttp from 'hooks/http.hook';
import { useAppDispatch } from 'store/store';
import EventSlice from 'store/slices/event.slice';

/**
 * Properties for the ResourceListModal component.
 * 
 * @interface ResourceListModalProps
 * @property {boolean} open - Whether modal is visible
 * @property {Function} onClose - Callback when modal is closed
 * @property {string} [facilityId] - Facility ID to load/save resources for
 * @property {string} [facilityName] - Facility name for display
 * @property {Function} onSaveResources - Callback with saved resources array
 * @property {boolean} [canDelete=false] - Whether delete functionality is enabled
 */
interface ResourceListModalProps {
  open: boolean;
  onClose: () => void;
  facilityId?: string;
  facilityName?: string;
  onSaveResources: (resources: Partial<ResourceInterface>[]) => void;
  canDelete?: boolean;
}

/**
 * Form data structure for resource creation.
 * 
 * @interface ResourceFormData
 * @property {string} name - Resource name (room number, equipment name)
 * @property {string} code - Resource code/identifier
 * @property {string} phone - Resource contact phone number
 * @property {Object} [defaultAppointmentProvider] - Default assigned provider
 */
interface ResourceFormData {
  name: string;
  code: string;
  phone: string;
  defaultAppointmentProvider?: { id: string };
}

/**
 * Extended resource interface with new resource indicator.
 * 
 * @interface ExtendedResourceInterface
 * @extends {Partial<ResourceInterface>}
 * @property {boolean} [isNew] - Flag indicating newly added resource (not yet saved)
 */
interface ExtendedResourceInterface extends Partial<ResourceInterface> {
  isNew?: boolean;
}

/**
 * Resource list modal component for managing facility resources.
 * Provides interface to add, view, and remove resources for a medical facility.
 * 
 * Features:
 * - Two-tab interface: Add New Resource and Resource List
 * - Formik-based resource creation form
 * - Resource table with new/existing visual indicators
 * - Doctor/provider autocomplete assignment
 * - Phone number validation
 * - Form validation with Yup schema
 * - Add resources to list without immediate save
 * - Remove resources from list
 * - Save all changes in batch
 * - Loading existing facility resources
 * - Duplicate code detection
 * - Resource list with doctor name resolution
 * - Visual distinction for new vs existing resources
 * 
 * Tabs:
 * 1. Add New Resource:
 *    - Name field (required)
 *    - Code field (required, checked for duplicates)
 *    - Phone field (validated format)
 *    - Default provider dropdown (optional)
 *    - Add to List button
 * 
 * 2. Resource List:
 *    - Table showing all resources
 *    - Status indicator (NEW/EXISTING)
 *    - Remove button per resource
 *    - Visual highlighting for new resources
 * 
 * Workflow:
 * 1. Open modal for facility
 * 2. Load existing facility resources
 * 3. Load available doctors
 * 4. User adds new resources via form
 * 5. Resources added to list with isNew flag
 * 6. User can remove resources
 * 7. Save button sends all resources to parent
 * 8. Parent handles actual persistence
 * 
 * Validation:
 * - Name required
 * - Code required and unique
 * - Phone format validation
 * - Duplicate code detection across new and existing
 * 
 * @param {ResourceListModalProps} props - Component properties
 * @returns {JSX.Element} Resource management modal dialog
 */
const ResourceListModal: React.FC<ResourceListModalProps> = ({
  open,
  onClose,
  facilityId,
  facilityName,
  onSaveResources,
  canDelete = false
}) => {
  const { addErrorMessage } = EventSlice.actions;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, _error, sendRequest] = useHttp();
  const [doctorList, setDoctorList] = useState<ProviderInterface[]>([]);
  const [resourceList, setResourceList] = useState<ExtendedResourceInterface[]>([]);
  const [existingResources, setExistingResources] = useState<ResourceInterface[]>([]);

  const initialValues: ResourceFormData = {
    name: '',
    code: '',
    phone: '',
    defaultAppointmentProvider: undefined
  };

  useEffect(() => {
    if (open) {
      sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorsHandler, "GET");
      
      if (facilityId) {
        sendRequest(
          `${CoreAPI_URLs.RESOURCES_GET_ALL_BY_FACILITY}`,
          onGetExistingResourcesHandler,
          "GET",
          null,
          [{ header: HeaderEnum.RESOURCE_ID, value: facilityId }]
        );
      }
    }
  }, [open, facilityId]);

  const onGetDoctorsHandler = (data: QueryResultInterface<ProviderInterface>) => {
    const sortedDoctors = data.results.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setDoctorList(sortedDoctors);
  };

  const onGetExistingResourcesHandler = (data: QueryResultInterface<ResourceInterface>) => {
    const existingResourcesWithFlag = data.results.map(resource => ({
      ...resource,
      isNew: false
    }));
    setExistingResources(data.results);
    setResourceList(existingResourcesWithFlag);
  };

  const doctorOptions = useMemo<AutoCompleteOption[]>(() => {
    if (doctorList.length === 0) {
      return [];
    } else {
      return doctorList
        .map((a) => ({
          label: `${a.person.name} ${a.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [doctorList]);

  const handleAddResource = (resourceData: ResourceFormData, resetForm: () => void, setFieldValue: any) => {
    if (!resourceData.name?.trim() || !resourceData.code?.trim()) {
      dispatch(addErrorMessage({ message:t('resourceList.nameAndCodeRequired'), id: crypto.randomUUID() }));
      return;
    }
    
    const isDuplicateCode = resourceList.some(resource => 
      resource.code?.toLowerCase() === resourceData.code.toLowerCase()
    );
    
    if (isDuplicateCode) {
      dispatch(addErrorMessage({ message:t('resourceList.duplicateCodeError'), id: crypto.randomUUID() }));
      return;
    }

    const newResource: ExtendedResourceInterface = {
      ...getNewResourceDataContent(),
      name: resourceData.name.trim(),
      code: resourceData.code.trim(),
      phone: resourceData.phone ? validatePhoneInputSymbol(resourceData.phone) : '',
      location: facilityId ? { id: facilityId } : {},
      defaultAppointmentProvider: resourceData.defaultAppointmentProvider || {},
      isNew: true
    };

    setResourceList(prev => [...prev, newResource]);
    
    resetForm();
    setFieldValue('name', '');
    setFieldValue('code', '');
    setFieldValue('phone', '');
    setFieldValue('defaultAppointmentProvider', undefined);
  };

  const handleRemoveResource = async (index: number) => {
    const resource = resourceList[index];
    
    if (!resource.isNew && resource.id) {
      if (!canDelete) {
        dispatch(addErrorMessage({ message: t('resourceList.noDeletePermission'), id: crypto.randomUUID() }));
        return;
      }

      const confirmed = window.confirm(t('resourceList.confirmDelete'));
      if (!confirmed) return;

      try {
        await new Promise((resolve, reject) => {
           sendRequest<boolean, ResourceInterface>(
            CoreAPI_URLs.RESOURCE_GENERAL_OPERATIONS,
            null,
            "DELETE",
            null,
            [{ header: HeaderEnum.RESOURCE_ID, value: resource.id }],
          );
        });
        setResourceList(prev => prev.filter((_, i) => i !== index));
        
        dispatch(addErrorMessage({ message: t('resourceList.deleteSuccess'), id: crypto.randomUUID() }));
      } catch (error) {
        console.error('Error deleting resource:', error);
       dispatch(addErrorMessage({ message:t('resourceList.deleteError'), id: crypto.randomUUID() }));
      }
    } else {
      setResourceList(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSaveAll = () => {
    const newResources = resourceList.filter(resource => resource.isNew);
    onSaveResources(newResources);
    setResourceList(prev => prev.filter(resource => !resource.isNew));
    onClose();
  };

  const handleCancel = () => {
    const existingResourcesWithFlag = existingResources.map(resource => ({
      ...resource,
      isNew: false
    }));
    setResourceList(existingResourcesWithFlag);
    onClose();
  };

  const newResourcesCount = resourceList.filter(r => r.isNew).length;
  const existingResourcesCount = resourceList.filter(r => !r.isNew).length;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={2}>
          <ListAltIcon />
          <Typography variant="h6">
            {t('resourceList.title')}:  {facilityName}
          </Typography>
          <Chip 
            label={`${existingResourcesCount} ${t('resourceList.existing')}`} 
            color="default" 
            size="small" 
          />
          {newResourcesCount > 0 && (
            <Chip 
              label={`${newResourcesCount} ${t('resourceList.new')}`} 
              color="success" 
              size="small" 
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('resourceList.addNewResource')}
            </Typography>
            <FormikForm
              key={resourceList.length}
              initialValues={initialValues}
              validationSchema={resourceFormYUPValidation(t)}
              onSubmit={() => {}}
            >
              {({ values, errors, touched, setFieldValue, resetForm }: FormikProps<ResourceFormData>) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormField
                        type="text"
                        label={t("resourceForm.name")}
                        name="name"
                        value={values.name}
                        error={errors.name && touched.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormField
                        type="text"
                        label={t("resourceForm.code")}
                        name="code"
                        value={values.code}
                        error={errors.code && touched.code}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormPhoneInput
                        key={`phone-${resourceList.length}`}
                        specialLabel={t("facilityForm.phone")}
                        country={"us"}
                        name="phone"
                        required={false}
                        value={values.phone}
                        onChange={(value) => {
                          setFieldValue('phone', value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        key={`doctor-${resourceList.length}`}
                        options={doctorOptions}
                        loading={isLoading}
                        name="defaultAppointmentProvider.id"
                        label={t("appointmentForm.doctorProvider")}
                        onChange={(_e, value) => {
                          setFieldValue("defaultAppointmentProvider", value ? { id: value.id } : undefined);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddResource(values, resetForm, setFieldValue)}
                        disabled={!values.name?.trim() || !values.code?.trim()}
                        fullWidth
                      >
                        {t('resourceList.addToList')}
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </FormikForm>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('resourceList.listItems')} ({resourceList.length})
            </Typography>
            {resourceList.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  {t('resourceList.emptyList')}
                </Typography>
              </Box>
            ) : (
              <ResourceTable
                modelList={resourceList}
                setModelList={setResourceList}
                doctorList={doctorList}
                onRemoveResource={handleRemoveResource}
                canDelete={canDelete}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="default">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSaveAll}
          variant="contained"
          color="primary"
          disabled={newResourcesCount === 0 || isLoading}
        >
          {t('resourceList.saveNewResources')} ({newResourcesCount})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceListModal;